import type { PatientEncounter } from 'src/patient/components/models/patient-encounter';

export type SavedInstallmentPrefill = {
  downPmtAmt: number;
  paymentDueAmt: number;
  billingFrequency: 'monthly' | 'weekly';
  /** Term in months for display / stored custom term (weekly API terms are in weeks). */
  termsInMonths: number;
};

/**
 * Latest non–paid-in-full installment program on the encounter, if any (for custom-results prefill).
 */
export function getSavedInstallmentProgramPrefillFromEncounter(
  activeEncounter: PatientEncounter | null | undefined
): SavedInstallmentPrefill | null {
  const list =
    activeEncounter?.patientPaymentProgram?.filter((p) => (p?.patientPaymentProgramId ?? 0) > 0) ?? [];
  const program = list.length > 0 ? list[list.length - 1] : undefined;
  const schedule = program?.patientPaymentSchedule;
  if (!program || !schedule) return null;

  const freqRaw = (schedule.paymentFreq || '').toString().toUpperCase();
  if (freqRaw === 'F' || program.isPaidInFull) return null;
  if (!(schedule.terms > 0 || schedule.paymentDueAmt > 0 || freqRaw === 'M' || freqRaw === 'W')) return null;

  const downPmtAmt = program.downPmtAmt ?? schedule.downPmtAmt ?? 0;
  const scheduleAny = schedule as unknown as Record<string, unknown>;
  const rawPaymentDue = schedule.paymentDueAmt ?? scheduleAny?.paymentDueAmt;
  const paymentDueAmt =
    typeof rawPaymentDue === 'number' && !Number.isNaN(rawPaymentDue)
      ? rawPaymentDue
      : Number(rawPaymentDue) || 0;

  if (downPmtAmt <= 0 && paymentDueAmt <= 0) return null;

  const isWeekly = freqRaw === 'W' || (schedule.paymentFreq || '').toString().toLowerCase() === 'weekly';
  const billingFrequency: 'monthly' | 'weekly' = isWeekly ? 'weekly' : 'monthly';
  const termsRaw = schedule.terms ?? (scheduleAny?.terms as number) ?? 0;
  const termsInMonths = isWeekly && termsRaw > 0 ? termsRaw / 4 : termsRaw;

  return {
    downPmtAmt,
    paymentDueAmt,
    billingFrequency,
    termsInMonths: termsInMonths > 0 ? Math.round(termsInMonths) : 0,
  };
}

export type CustomResultsNavState = {
  showDownPayment: boolean;
  showRecurringAmount: boolean;
  prefillFromSavedProgram?: boolean;
  savedDownPmtAmt?: number;
  savedPaymentDueAmt?: number;
  savedBillingFrequency?: 'monthly' | 'weekly';
  savedTermsInMonths?: number;
};

/** State for navigate(custom-results): includes saved program amounts when an installment program exists. */
export function buildCustomResultsNavigationState(
  showDownPayment: boolean,
  showRecurringAmount: boolean,
  activeEncounter: PatientEncounter | null | undefined
): CustomResultsNavState {
  const base: CustomResultsNavState = { showDownPayment, showRecurringAmount };
  const saved = getSavedInstallmentProgramPrefillFromEncounter(activeEncounter);
  if (!saved) return base;
  return {
    ...base,
    prefillFromSavedProgram: true,
    savedDownPmtAmt: saved.downPmtAmt,
    savedPaymentDueAmt: saved.paymentDueAmt,
    savedBillingFrequency: saved.billingFrequency,
    savedTermsInMonths: saved.termsInMonths > 0 ? saved.termsInMonths : undefined,
  };
}
